// src/modules/dl/DiffusionLab.jsx
// Diffusion Model Lab — fully visual
// Forward diffusion · Reverse denoising · Noise schedules · Score function · Latent space

import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import { Panel, Metric, LogPanel, timestamp } from '../../components/ui.jsx';

const AC = 'var(--violet)';
const P  = ['#a78bfa','#22d3ee','#4ade80','#fbbf24','#f87171','#fb923c'];

const Btn = ({onClick,children,color=AC,disabled,style}) => (
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'22',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,letterSpacing:'.08em',opacity:disabled?.4:1,...style}}>{children}</button>
);
const Sl = ({label,value,min,max,step=0.01,onChange,unit='',color=AC}) => (
  <div style={{marginBottom:'10px'}}>
    <div style={{display:'flex',justifyContent:'space-between',marginBottom:'3px'}}>
      <span style={{fontSize:'10px',color:'var(--t2)'}}>{label}</span>
      <span style={{fontSize:'10px',color,fontWeight:700}}>{typeof value==='number'?value.toFixed(step<0.01?3:step<1?2:0):value}{unit}</span>
    </div>
    <div style={{position:'relative',height:'3px',background:'var(--b0)',borderRadius:'2px'}}>
      <div style={{position:'absolute',left:0,height:'100%',background:`linear-gradient(90deg,${color}44,${color})`,width:`${((value-min)/(max-min))*100}%`}}/>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e=>onChange(parseFloat(e.target.value))}
        style={{position:'absolute',inset:'-7px 0',width:'100%',opacity:0,cursor:'pointer',height:'18px'}}/>
    </div>
  </div>
);
const Sel = ({label,value,onChange,options}) => (
  <div style={{marginBottom:'8px'}}>
    {label&&<div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'3px'}}>{label}</div>}
    <select value={value} onChange={e=>onChange(e.target.value)} style={{width:'100%',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'6px 8px',fontSize:'11px',fontFamily:'JetBrains Mono',borderRadius:'2px',outline:'none'}}>
      {options.map(o=><option key={o.value??o} value={o.value??o}>{o.label??o}</option>)}
    </select>
  </div>
);

// ─── MATH ─────────────────────────────────────────────────────────────────────
const randn = () => { let u=0,v=0; while(!u)u=Math.random(); while(!v)v=Math.random(); return Math.sqrt(-2*Math.log(u))*Math.cos(2*Math.PI*v); };

// Noise schedules: beta(t) at timestep t/T
const betaSchedule = (type, t, T) => {
  const s = t/T;
  if(type==='linear') return 0.0001 + s*(0.02-0.0001);
  if(type==='cosine') return 0.02*0.5*(1-Math.cos(Math.PI*s));
  if(type==='quadratic') return 0.0001 + Math.pow(s,2)*(0.02-0.0001);
  if(type==='sigmoid') { const x=s*12-6; return 0.0001 + 0.02/(1+Math.exp(-x)); }
  return 0.01;
};

// Cumulative alpha_bar(t) = prod(1-beta_i) for i=0..t
const alphaBar = (type, t, T) => {
  let ab=1;
  for(let i=0;i<=t;i++) ab*=(1-betaSchedule(type,i,T));
  return ab;
};

// Forward process: q(x_t|x_0) = sqrt(alpha_bar)*x0 + sqrt(1-alpha_bar)*eps
const forwardStep = (x0, t, T, schedType) => {
  const ab = alphaBar(schedType, t, T);
  const eps = x0.map(()=>randn());
  const xt = x0.map((v,i)=>Math.sqrt(ab)*v + Math.sqrt(1-ab)*eps[i]);
  return {xt, eps, noiseLevel:Math.sqrt(1-ab), signalLevel:Math.sqrt(ab)};
};

// Fake score estimate: push toward origin + small correction
const scoreEstimate = (xt, t, T, schedType) => {
  const ab = alphaBar(schedType, t, T);
  return xt.map(v => -v*(1-ab)/(1-ab+0.1));
};

// Simple 2D "image" — a 12×12 grid representing a circle signal
const genSignalImage = (size=12) => {
  return Array.from({length:size},(_,i)=>Array.from({length:size},(_,j)=>{
    const cx=size/2-0.5, cy=size/2-0.5;
    const d=Math.sqrt((i-cx)**2+(j-cy)**2);
    return d<size/3?1: d<size/2?0.4:0;
  }));
};

// Apply noise to 2D image
const noisifyImage = (img, noiseLevel, signalLevel) => {
  return img.map(row=>row.map(v=>signalLevel*v+noiseLevel*randn()));
};

const TABS = ['forward','reverse','noise-schedule','score-function','ddim-vs-ddpm'];

// ─── 2D PIXEL GRID ───────────────────────────────────────────────────────────
function PixelGrid({matrix, title, color='#a78bfa', cellSize=14}) {
  if(!matrix?.length) return null;
  const flat=matrix.flat(), mn=Math.min(...flat), mx=Math.max(...flat), rng=mx-mn||1;
  return (
    <div style={{display:'flex',flexDirection:'column',alignItems:'flex-start'}}>
      <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'4px',letterSpacing:'.08em'}}>{title}</div>
      <div style={{display:'inline-flex',flexDirection:'column',gap:'1px',border:'1px solid #1e293b',borderRadius:'4px',overflow:'hidden'}}>
        {matrix.map((row,i)=>(
          <div key={i} style={{display:'flex',gap:'1px'}}>
            {row.map((v,j)=>{
              const norm=(v-mn)/rng;
              return <div key={j} style={{width:cellSize,height:cellSize,background:`${color}${Math.round((0.04+norm*0.96)*255).toString(16).padStart(2,'0')}`,flexShrink:0}}/>;
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── DIFFUSION TIMELINE SVG ──────────────────────────────────────────────────
function DiffusionTimeline({steps, direction='forward'}) {
  const N=steps.length, W=700, H=100, pad=30;
  const xStep=(W-pad*2)/(N-1);
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{background:'#060b14',borderRadius:'6px',border:'1px solid #1e293b',display:'block',marginBottom:8}}>
      {/* Connecting line */}
      <line x1={pad} y1={H/2} x2={W-pad} y2={H/2} stroke="#334155" strokeWidth={2}/>
      {/* Steps */}
      {steps.map((s,i)=>{
        const x=pad+i*xStep;
        const noiseAlpha=direction==='forward'?s.noiseLevel:1-s.noiseLevel;
        const sigAlpha=1-noiseAlpha;
        const r=12+noiseAlpha*8;
        return (
          <g key={i}>
            {/* Noise circle (outer) */}
            <circle cx={x} cy={H/2} r={r} fill={`rgba(248,113,113,${0.1+noiseAlpha*0.9})`} stroke="#f87171" strokeWidth={1}/>
            {/* Signal circle (inner) */}
            <circle cx={x} cy={H/2} r={r*sigAlpha} fill={`rgba(167,139,250,${0.1+sigAlpha*0.9})`} stroke="#a78bfa" strokeWidth={1}/>
            {/* Label */}
            <text x={x} y={H-8} textAnchor="middle" style={{fontSize:'8px',fill:'var(--t3)',fontFamily:'JetBrains Mono'}}>t={s.t}</text>
          </g>
        );
      })}
      {/* Direction arrow */}
      <text x={W/2} y={14} textAnchor="middle" style={{fontSize:'10px',fill:'#64748b',fontFamily:'JetBrains Mono'}}>
        {direction==='forward'?'FORWARD: x₀ → xₜ (add noise)':'REVERSE: xₜ → x₀ (denoise)'}
      </text>
    </svg>
  );
}

export default function DiffusionLab() {
  const [tab, setTab] = useState('forward');
  const [logs, setLogs] = useState([{ts:'00:00:00',msg:'Diffusion Lab — watch noise get added and removed step by step.',t:'info'}]);
  const log = (msg,t='info') => setLogs(l=>[...l.slice(-80),{ts:timestamp(),msg,t}]);

  const [T, setT] = useState(20);
  const [schedType, setSchedType] = useState('cosine');
  const [viewT, setViewT] = useState(10);
  const [forwardResult, setForwardResult] = useState(null);
  const [reverseResult, setReverseResult] = useState(null);
  const [schedResult, setSchedResult] = useState(null);
  const [scoreResult, setScoreResult] = useState(null);
  const [ddimResult, setDdimResult] = useState(null);

  const [cfgScale, setCfgScale] = useState(7.5);
  const [samplerSteps, setSamplerSteps] = useState(20);

  const baseImage = genSignalImage(12);

  const runForward = () => {
    const keyTs=[0, Math.floor(T*0.1), Math.floor(T*0.25), Math.floor(T*0.5), Math.floor(T*0.75), Math.floor(T*0.9), T-1];
    const steps = keyTs.map(t=>{
      const ab=alphaBar(schedType,t,T);
      const ns=Math.sqrt(1-ab), sl=Math.sqrt(ab);
      const img=noisifyImage(baseImage,ns,sl);
      return {t, noiseLevel:ns, signalLevel:sl, img, ab:+ab.toFixed(4)};
    });
    setForwardResult(steps);
    log(`Forward diffusion: T=${T} sched=${schedType} | ᾱ(T)=${steps.at(-1).ab}`,'ok');
  };

  const runReverse = () => {
    // Start from pure noise and denoise
    const pure = Array.from({length:12},()=>Array.from({length:12},()=>randn()));
    const steps=[];
    let xt=pure.map(r=>[...r]);

    const keyTs=[T-1, Math.floor(T*0.8), Math.floor(T*0.6), Math.floor(T*0.4), Math.floor(T*0.2), Math.floor(T*0.05), 0];
    keyTs.forEach(t=>{
      const ab=alphaBar(schedType,t,T);
      const ns=Math.sqrt(1-ab), sl=Math.sqrt(ab);
      // Denoise step: move toward signal
      xt=xt.map((row,i)=>row.map((v,j)=>v*0.85+baseImage[i][j]*0.15+randn()*ns*0.1));
      steps.push({t, noiseLevel:1-ns, signalLevel:sl, img:xt.map(r=>[...r]), ab:+ab.toFixed(4)});
    });
    setReverseResult(steps);
    log(`Reverse denoising: T=${T} | starting from pure noise → reconstructed signal`,'ok');
  };

  const runSchedules = () => {
    const scheds=['linear','cosine','quadratic','sigmoid'];
    const pts=Array.from({length:T+1},(_,t)=>{
      const pt={t};
      scheds.forEach(s=>{ pt[s]=+alphaBar(s,t,T).toFixed(4); });
      return pt;
    });
    const noisePts=Array.from({length:T+1},(_,t)=>{
      const pt={t};
      scheds.forEach(s=>{ pt[s]=+(1-alphaBar(s,t,T)).toFixed(4); });
      return pt;
    });
    setSchedResult({pts,noisePts,scheds});
    log(`Noise schedules: ${scheds.join(', ')} over T=${T} steps`,'ok');
  };

  const runScore = () => {
    // 1D score function visualization
    const xs=Array.from({length:80},(_,i)=>-4+i*0.1);
    // Data distribution: bimodal N(-1.5,0.5) + N(1.5,0.5)
    const dataDist=xs.map(x=>0.5*Math.exp(-0.5*(x+1.5)**2/0.25)+0.5*Math.exp(-0.5*(x-1.5)**2/0.25));
    const dataNorm=dataDist.reduce((s,v)=>s+v,0)*0.1;
    const normDist=dataDist.map(v=>v/dataNorm);

    // Score = gradient of log p(x) ≈ (mu-x)/sigma^2 for each mode
    const score=xs.map((x,i)=>{
      const p1=0.5*Math.exp(-0.5*(x+1.5)**2/0.25)+0.5*Math.exp(-0.5*(x-1.5)**2/0.25)+1e-10;
      const dp1=0.5*(-(x+1.5)/0.25)*Math.exp(-0.5*(x+1.5)**2/0.25)+0.5*(-(x-1.5)/0.25)*Math.exp(-0.5*(x-1.5)**2/0.25);
      return dp1/p1;
    });

    setScoreResult({xs,normDist,score,pts:xs.map((x,i)=>({x:+x.toFixed(2),density:+normDist[i].toFixed(4),score:+Math.max(-5,Math.min(5,score[i])).toFixed(4)}))});
    log(`Score function: ∇_x log p(x) of bimodal Gaussian`,'ok');
  };

  const runDDIM = () => {
    const ddimSteps=[5,10,20,50,100,200,500,1000];
    const ddpmFID=ddimSteps.map(s=>({steps:s,ddpm:+(3+50/s+randn()*0.5).toFixed(2),ddim:+(2.5+15/s+randn()*0.3).toFixed(2)}));
    setDdimResult(ddpmFID);
    log(`DDIM vs DDPM: FID scores at different step counts`,'ok');
  };

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=><button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?AC:'transparent'}`,background:tab===t?AC+'18':'transparent',color:tab===t?AC:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>)}
      </div>

      {/* Controls */}
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr auto',gap:'10px',padding:'12px',background:'var(--surface)',border:'1px solid var(--b0)',borderRadius:'4px',alignItems:'end'}}>
        <Sl label="Total timesteps T" value={T} min={10} max={100} step={5} onChange={setT} color={AC}/>
        <Sel label="NOISE SCHEDULE" value={schedType} onChange={setSchedType} options={['linear','cosine','quadratic','sigmoid'].map(v=>({value:v,label:v.toUpperCase()}))}/>
        <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.6,padding:'4px 0'}}>
          ᾱ(T) = {alphaBar(schedType,T-1,T).toFixed(4)} | noise at T: {(Math.sqrt(1-alphaBar(schedType,T-1,T))*100).toFixed(1)}%
        </div>
        <div style={{display:'flex',gap:'6px'}}>
          <Btn onClick={runForward} color={AC}>FORWARD →</Btn>
          <Btn onClick={runReverse} color="#4ade80">REVERSE →</Btn>
        </div>
      </div>

      {/* ── FORWARD ── */}
      {tab==='forward'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          {forwardResult
            ? <>
                <Panel title="FORWARD PROCESS — q(xₜ|x₀) = √ᾱ·x₀ + √(1-ᾱ)·ε — IMAGE GETS NOISIER" accent={AC}>
                  <div style={{marginBottom:'10px',fontSize:'10px',color:'var(--t3)'}}>Each step: more Gaussian noise added. By t=T the signal is gone — pure noise.</div>
                  <DiffusionTimeline steps={forwardResult} direction="forward"/>
                  <div style={{display:'flex',gap:'16px',overflowX:'auto',padding:'8px 0'}}>
                    {forwardResult.map((s,i)=>(
                      <div key={i} style={{display:'flex',flexDirection:'column',gap:'5px',alignItems:'center'}}>
                        <PixelGrid matrix={s.img} title={`t=${s.t}`} color={s.noiseLevel>0.7?'#f87171':s.noiseLevel>0.3?'#fbbf24':'#a78bfa'} cellSize={12}/>
                        <div style={{fontSize:'8px',color:'#64748b',textAlign:'center',fontFamily:'JetBrains Mono'}}>
                          noise:{(s.noiseLevel*100).toFixed(0)}%
                        </div>
                      </div>
                    ))}
                  </div>
                </Panel>

                <Panel title="SIGNAL vs NOISE LEVEL OVER TIMESTEPS" accent="#fbbf24">
                  <ResponsiveContainer width="100%" height={160}>
                    <AreaChart data={Array.from({length:T+1},(_,t)=>{const ab=alphaBar(schedType,t,T);return{t,signal:+Math.sqrt(ab).toFixed(4),noise:+Math.sqrt(1-ab).toFixed(4)};})}>
                      <defs>
                        <linearGradient id="sigG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#a78bfa" stopOpacity={0.5}/><stop offset="95%" stopColor="#a78bfa" stopOpacity={0}/></linearGradient>
                        <linearGradient id="nzG" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#f87171" stopOpacity={0.5}/><stop offset="95%" stopColor="#f87171" stopOpacity={0}/></linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="t" tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,1]}/>
                      <Tooltip/>
                      <Legend/>
                      <Area type="monotone" dataKey="signal" stroke="#a78bfa" fill="url(#sigG)" strokeWidth={2} name="√ᾱ (signal)"/>
                      <Area type="monotone" dataKey="noise" stroke="#f87171" fill="url(#nzG)" strokeWidth={2} name="√(1-ᾱ) (noise)"/>
                    </AreaChart>
                  </ResponsiveContainer>
                </Panel>
              </>
            : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Click FORWARD to watch noise be added step by step</div>
          }
          <LogPanel logs={logs} height={70}/>
        </div>
      )}

      {/* ── REVERSE ── */}
      {tab==='reverse'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          {reverseResult
            ? <>
                <Panel title="REVERSE PROCESS — pθ(xₜ₋₁|xₜ) — DENOISING FROM PURE NOISE → SIGNAL" accent="#4ade80">
                  <div style={{marginBottom:'10px',fontSize:'10px',color:'var(--t3)'}}>Neural net learns to predict the noise ε added at each step, then subtracts it. Iteratively reconstructs the original.</div>
                  <DiffusionTimeline steps={reverseResult} direction="reverse"/>
                  <div style={{display:'flex',gap:'16px',overflowX:'auto',padding:'8px 0'}}>
                    {reverseResult.map((s,i)=>(
                      <div key={i} style={{display:'flex',flexDirection:'column',gap:'5px',alignItems:'center'}}>
                        <PixelGrid matrix={s.img} title={`t=${s.t}`} color={s.signalLevel>0.7?'#4ade80':s.signalLevel>0.4?'#22d3ee':'#f87171'} cellSize={12}/>
                        <div style={{fontSize:'8px',color:'#64748b',textAlign:'center',fontFamily:'JetBrains Mono'}}>
                          signal:{(s.signalLevel*100).toFixed(0)}%
                        </div>
                      </div>
                    ))}
                  </div>
                </Panel>
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px'}}>
                  <Metric label="T steps" value={T} color={AC}/>
                  <Metric label="Schedule" value={schedType} color="#fbbf24"/>
                  <Metric label="Final ᾱ" value={alphaBar(schedType,T-1,T).toFixed(4)} color="#4ade80"/>
                  <Metric label="Noise at T" value={(Math.sqrt(1-alphaBar(schedType,T-1,T))*100).toFixed(1)+'%'} color="#f87171"/>
                </div>
              </>
            : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Click REVERSE to watch the denoising process</div>
          }
        </div>
      )}

      {/* ── NOISE SCHEDULE ── */}
      {tab==='noise-schedule'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <div style={{display:'flex',gap:'8px',marginBottom:'-4px'}}>
            <Btn onClick={runSchedules} color={AC}>PLOT ALL SCHEDULES →</Btn>
          </div>
          {schedResult
            ? <>
                <Panel title="CUMULATIVE SIGNAL ᾱ(t) — HOW FAST SIGNAL IS DESTROYED" accent={AC}>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={schedResult.pts}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="t" tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,1]}/>
                      <Tooltip/>
                      <Legend/>
                      {schedResult.scheds.map((s,i)=><Line key={s} type="monotone" dataKey={s} stroke={P[i]} strokeWidth={2} dot={false} name={s}/>)}
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
                <Panel title="CUMULATIVE NOISE (1-ᾱ) — HOW FAST NOISE BUILDS UP" accent="#f87171">
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={schedResult.noisePts}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="t" tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,1]}/>
                      <Tooltip/>
                      <Legend/>
                      {schedResult.scheds.map((s,i)=><Line key={s} type="monotone" dataKey={s} stroke={P[i]} strokeWidth={2} dot={false} name={s}/>)}
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px'}}>
                  {schedResult.scheds.map((s,i)=>(
                    <Panel key={s} title={s.toUpperCase()} accent={P[i]}>
                      <div style={{fontSize:'9px',color:'var(--t3)',lineHeight:1.7}}>
                        {s==='linear'&&'Constant β increase. Simple but destroys signal early.'}
                        {s==='cosine'&&'Slow at start/end. DDPM improved. Most popular.'}
                        {s==='quadratic'&&'Faster early, slower later. Less common.'}
                        {s==='sigmoid'&&'Smooth S-curve. Good middle ground.'}
                      </div>
                      <div style={{marginTop:'6px',fontSize:'10px',color:P[i],fontFamily:'JetBrains Mono',fontWeight:700}}>
                        ᾱ(T/2) = {alphaBar(s,Math.floor(T/2),T).toFixed(3)}
                      </div>
                    </Panel>
                  ))}
                </div>
              </>
            : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Plot all schedules</div>
          }
        </div>
      )}

      {/* ── SCORE FUNCTION ── */}
      {tab==='score-function'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Btn onClick={runScore} color="#22d3ee" style={{alignSelf:'flex-start'}}>COMPUTE SCORE FUNCTION →</Btn>
          {scoreResult
            ? <>
                <Panel title="DATA DISTRIBUTION + SCORE FUNCTION ∇_x log p(x)" accent="#22d3ee">
                  <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px'}}>Score function = gradient of log-density. Points toward high-density regions. Diffusion models learn this.</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={scoreResult.pts}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="x" tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <YAxis yAxisId="left" tick={{fontSize:9,fill:'var(--t3)'}} domain={[0,'auto']}/>
                      <YAxis yAxisId="right" orientation="right" tick={{fontSize:9,fill:'var(--t3)'}} domain={[-5,5]}/>
                      <Tooltip/>
                      <Legend/>
                      <ReferenceLine yAxisId="left" x={0} stroke="#334155"/>
                      <Line yAxisId="left" type="monotone" dataKey="density" stroke="#a78bfa" strokeWidth={2.5} dot={false} name="p(x) density"/>
                      <Line yAxisId="right" type="monotone" dataKey="score" stroke="#22d3ee" strokeWidth={2} dot={false} name="∇_x log p(x)"/>
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}}>
                  <Panel title="HOW DIFFUSION USES SCORE" accent="#a78bfa">
                    <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.8}}>
                      <div style={{color:'#a78bfa',fontWeight:700,marginBottom:'4px'}}>Training:</div>
                      <div>Model learns εθ(xₜ,t) ≈ noise added at step t</div>
                      <div>Equivalent to learning score: s = -εθ/√(1-ᾱ)</div>
                      <div style={{color:'#a78bfa',fontWeight:700,margin:'8px 0 4px'}}>Sampling:</div>
                      <div>Use Langevin dynamics guided by score to walk toward data distribution.</div>
                      <div style={{color:'#22d3ee',margin:'8px 0 4px'}}>Langevin step:</div>
                      <div style={{fontFamily:'JetBrains Mono',fontSize:'9px'}}>xₜ₋₁ = xₜ + δ·s(xₜ) + √(2δ)·z</div>
                    </div>
                  </Panel>
                  <Panel title="CLASSIFIER-FREE GUIDANCE" accent="#fbbf24">
                    <Sl label="CFG Scale" value={cfgScale} min={1} max={20} step={0.5} onChange={setCfgScale} color="#fbbf24"/>
                    <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.8,marginTop:'8px'}}>
                      <div style={{fontFamily:'JetBrains Mono',fontSize:'9px',color:'#fbbf24',marginBottom:'4px'}}>ε̃ = εunconditional + w·(εconditional - εunconditional)</div>
                      w = {cfgScale}
                      <div style={{marginTop:'6px'}}>
                        {cfgScale<3&&'Low guidance: creative but off-prompt'}
                        {cfgScale>=3&&cfgScale<10&&'Medium: balanced quality and fidelity'}
                        {cfgScale>=10&&'High: very faithful to prompt, less diversity'}
                      </div>
                    </div>
                  </Panel>
                </div>
              </>
            : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Compute the score function</div>
          }
        </div>
      )}

      {/* ── DDIM vs DDPM ── */}
      {tab==='ddim-vs-ddpm'&&(
        <div style={{display:'flex',flexDirection:'column',gap:'10px'}}>
          <Btn onClick={runDDIM} color="#fb923c" style={{alignSelf:'flex-start'}}>RUN COMPARISON →</Btn>
          {ddimResult
            ? <>
                <Panel title="FID SCORE (LOWER = BETTER QUALITY) vs SAMPLING STEPS" accent="#fb923c">
                  <div style={{fontSize:'10px',color:'var(--t3)',marginBottom:'8px'}}>DDIM: deterministic, 10-50 steps. DDPM: stochastic, needs 1000 steps. Same trained model.</div>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={ddimResult}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                      <XAxis dataKey="steps" tick={{fontSize:9,fill:'var(--t3)'}} scale="log" domain={['auto','auto']}/>
                      <YAxis tick={{fontSize:9,fill:'var(--t3)'}}/>
                      <Tooltip/>
                      <Legend/>
                      <Line type="monotone" dataKey="ddpm" stroke="#f87171" strokeWidth={2} dot={{r:4}} name="DDPM (stochastic)"/>
                      <Line type="monotone" dataKey="ddim" stroke="#22d3ee" strokeWidth={2} dot={{r:4}} name="DDIM (deterministic)"/>
                    </LineChart>
                  </ResponsiveContainer>
                </Panel>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
                  {[
                    {name:'DDPM',color:'#f87171',steps:'1000',pros:'Highest quality possible, diverse samples',cons:'Very slow — 1000 NFE per image',eq:'xₜ₋₁ = μθ(xₜ,t) + σₜ·z'},
                    {name:'DDIM',color:'#22d3ee',steps:'10-50',pros:'10-100× faster, deterministic, interpolatable',cons:'Slightly lower diversity than DDPM',eq:'xₜ₋₁ = √ᾱₜ₋₁·x̂₀ + direction + σₜz'},
                    {name:'LCM/LCM-LoRA',color:'#4ade80',steps:'1-4',pros:'Real-time (4 NFE!). Distilled from DDIM.',cons:'Lower quality, needs fine-tuning',eq:'Consistency model: f(xₜ,t) = xₜ₀'},
                  ].map(m=>(
                    <Panel key={m.name} title={m.name} accent={m.color}>
                      <div style={{fontSize:'9px',color:'var(--t2)',fontFamily:'JetBrains Mono',padding:'6px',background:'var(--deep)',borderRadius:'3px',marginBottom:'8px'}}>{m.eq}</div>
                      {[['Steps',m.steps,m.color],['Pro',m.pros,'#4ade80'],['Con',m.cons,'#f87171']].map(([k,v,c])=>(
                        <div key={k} style={{marginBottom:'5px',fontSize:'9px'}}>
                          <span style={{color:'var(--t3)',marginRight:6}}>{k}:</span><span style={{color:c}}>{v}</span>
                        </div>
                      ))}
                    </Panel>
                  ))}
                </div>
              </>
            : <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'300px',color:'var(--t3)',fontSize:'12px',border:'1px dashed var(--b1)',borderRadius:'4px'}}>← Run comparison</div>
          }
          <LogPanel logs={logs} height={70}/>
        </div>
      )}
    </div>
  );
}
